/**
 * 12-word mnemonic entry component for wallet restore.
 *
 * Displays a 3x4 grid of numbered input fields. Validates the complete
 * mnemonic on submit using BIP-39 checksum verification. Supports pasting
 * a full 12-word phrase into the first field, which auto-fills all fields.
 *
 * Tier 1 component - used during wallet restore flow.
 *
 * @module MnemonicEntry
 */

import { useCallback, useRef, useState } from 'react';
import type { ChangeEvent, ClipboardEvent, FormEvent, ReactNode } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { validateMnemonic } from '@/lib/crypto';

/** Total number of words in a BIP-39 12-word mnemonic. */
const WORD_COUNT = 12;

/** Props for the MnemonicEntry component. */
interface MnemonicEntryProps {
  /** Called with the validated 12-word mnemonic string on successful submit. */
  readonly onComplete: (mnemonic: string) => void;
  /** External error message to display (e.g., from restore hook). */
  readonly error?: string;
  /** Whether to show loading state on the submit button. */
  readonly loading?: boolean;
}

/**
 * 12-word mnemonic input grid with validation and paste support.
 *
 * Layout: 3 columns x 4 rows of numbered input fields.
 * Validates the full phrase on submit via BIP-39 checksum.
 * Supports pasting a space-separated 12-word phrase into any field.
 *
 * Tier 1 - wallet restore flow.
 */
export function MnemonicEntry({
  onComplete,
  error,
  loading = false,
}: MnemonicEntryProps): ReactNode {
  const [words, setWords] = useState<string[]>(Array.from({ length: WORD_COUNT }, () => ''));
  const [validationError, setValidationError] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const displayError = error ?? validationError;
  const allFilled = words.every((w) => w.trim() !== '');

  const handleWordChange = useCallback(
    (index: number, event: ChangeEvent<HTMLInputElement>): void => {
      const value = event.target.value.toLowerCase().trim();
      setWords((prev) => {
        const next = [...prev];
        next[index] = value;
        return next;
      });
      setValidationError(null);
    },
    [],
  );

  const handlePaste = useCallback(
    (index: number, event: ClipboardEvent<HTMLInputElement>): void => {
      const text = event.clipboardData.getData('text').trim().toLowerCase();
      const pastedWords = text.split(/\s+/);

      if (pastedWords.length === WORD_COUNT) {
        event.preventDefault();
        setWords(pastedWords);
        setValidationError(null);
        // Focus the last input after paste
        const lastInput = inputRefs.current[WORD_COUNT - 1];
        if (lastInput) {
          lastInput.focus();
        }
      } else if (pastedWords.length > 1 && index + pastedWords.length <= WORD_COUNT) {
        // Partial paste: fill from current index forward
        event.preventDefault();
        setWords((prev) => {
          const next = [...prev];
          pastedWords.forEach((word, i) => {
            next[index + i] = word;
          });
          return next;
        });
        setValidationError(null);
      }
    },
    [],
  );

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      const mnemonic = words.map((w) => w.trim()).join(' ');

      if (!validateMnemonic(mnemonic)) {
        setValidationError('Invalid recovery phrase. Please check your words and try again.');
        return;
      }

      onComplete(mnemonic);
    },
    [words, onComplete],
  );

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <div className="grid grid-cols-3 gap-3">
        {words.map((word, index) => (
          <div key={index} className="flex flex-col gap-1">
            <label
              htmlFor={`mnemonic-word-${String(index)}`}
              className="text-xs text-muted-foreground font-medium"
            >
              Word {String(index + 1)}
            </label>
            <Input
              id={`mnemonic-word-${String(index)}`}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              value={word}
              onChange={(e) => {
                handleWordChange(index, e);
              }}
              onPaste={(e) => {
                handlePaste(index, e);
              }}
              aria-label={`Word ${String(index + 1)}`}
              autoComplete="off"
              data-1p-ignore=""
              spellCheck={false}
              autoCapitalize="none"
              className="text-sm"
            />
          </div>
        ))}
      </div>

      {displayError ? (
        <p className="text-sm text-destructive text-center" role="alert" aria-live="assertive">
          {displayError}
        </p>
      ) : null}

      <Button type="submit" disabled={!allFilled || loading} loading={loading} size="lg">
        Restore Wallet
      </Button>
    </form>
  );
}
